/**
 * O caminho que alguém salvou e que não existe (ISSUE-210).
 *
 * `/approvals` e `/teams` estavam na barra lateral — um deles com uma etiqueta
 * `3` escrita à mão — e **não tinham rota nem página**. Quem clicava era
 * devolvido em silêncio para a raiz pelo catch-all, sem descobrir se errou o
 * link, se perdeu acesso, ou se a tela sumiu.
 *
 * **NEM 404 NEM 410 DESCREVEM ESTE CASO.** O RFC 9110 separa "não achei" de
 * "existia e foi removido"; aqui a tela **nunca existiu**, e a ausência é
 * conhecida e deliberada. O RFC informa a redação, não o código de status — a
 * resposta é da tela, porque isto é aplicação de página única atrás de login.
 *
 * **O TEXTO PASSA PELO i18n COMO O RESTO DO PRODUTO.** A primeira versão trazia
 * as frases cravadas em pt-BR num produto que fala três idiomas — e no mesmo
 * PR que traduzia as strings do painel. Quem usa em inglês abria `/approvals` e
 * lia "Aprovações ainda não existe". Achado na revisão de código.
 */
import { ArrowLeft, Construction } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'

/** A chave do nome que a pessoa viu no menu, pelo caminho que ela abriu. */
const RECURSOS: Record<string, string> = {
  '/approvals': 'recursoIndisponivel.aprovacoes',
  '/teams': 'recursoIndisponivel.times',
}

export function RecursoIndisponivelPage() {
  const { t } = useTranslation('common')
  const { pathname } = useLocation()
  const chave = RECURSOS[pathname]
  // Caminho desconhecido cai no texto sem nome — a página serve a rota órfã
  // conhecida, e não pode quebrar se ganhar uma terceira sem o mapa atualizado.
  const nome = chave ? t(chave) : undefined

  return (
    <div className="flex items-center justify-center py-16">
      <Card variant="glass" className="max-w-lg p-8 text-center">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-500/15">
          <Construction className="h-7 w-7 text-amber-400" />
        </div>

        <h1 className="text-xl font-semibold text-gray-100">
          {nome
            ? t('recursoIndisponivel.tituloComNome', { nome })
            : t('recursoIndisponivel.tituloSemNome')}
        </h1>

        <p className="mt-3 text-sm leading-relaxed text-gray-400">
          {nome
            ? t('recursoIndisponivel.explicacaoComNome', { nome })
            : t('recursoIndisponivel.explicacaoSemNome')}
        </p>

        <p className="mt-2 text-sm text-gray-500">{t('recursoIndisponivel.linkSalvo')}</p>

        <Button variant="outline" className="mt-6 gap-2" asChild>
          <Link to="/dashboard">
            <ArrowLeft className="h-4 w-4" />
            {t('recursoIndisponivel.voltar')}
          </Link>
        </Button>
      </Card>
    </div>
  )
}
